import React from 'react'
import { Link } from 'react-router-dom'
import {
  COLUMN_TYPES,
  COLUMN_SORT_SETTING,
  DATA_TYPES,
  SEARCH_TYPES,
  LOOKUP_TYPES,
  Icon,
  reducers,
  coreConstants,
} from '@adc/ui-morpheus'
import { EVENTS } from '../../constants/tableTypes'
import { eventType as eventTypeList } from '../../constants/optionLists'

const { yesNo: yesNoList } = coreConstants.optionLists
const defaultColPropsWithWidth300 = {
  width: 300,
  isResizable: true,
  isReorderable: true,
  isVisible: false,
}
const defaultColPropsWithWidth100 = {
  width: 100,
  isResizable: true,
  isReorderable: true,
  isVisible: false,
}

export const tableConfig = {
  config: {
    entityKey: EVENTS,
    primaryKeyField: 'event_id',
    allowExportCSV: true,
    enableSavedSearches: true,
  },
  fieldDefinitions: [
    {
      name: 'Selected',
      apiRef: 'selected', // not api val
      isUiOnly: true, // exclude this field from ES DSL search list
      columnDefinition: {
        type: COLUMN_TYPES.CHECKBOX,
        colProps: { width: 40, fixed: true },
      },
    },
    {
      name: 'Event Code',
      apiRef: 'event_code',
      columnDefinition: {
        type: COLUMN_TYPES.TEXT,
        partialMatch: true,
        colProps: {
          ...defaultColPropsWithWidth300,
          isVisible: true,
          searchable: true,
        },
      },
    },
    {
      name: 'Title',
      apiRef: 'title',
      columnDefinition: {
        type: COLUMN_TYPES.TEXT,
        partialMatch: true,
        colProps: {
          ...defaultColPropsWithWidth300,
          isVisible: true,
          sort: COLUMN_SORT_SETTING.SORTED,
          searchable: true,
          sortAscending: true,
        },
      },
    },
    {
      name: 'Campaign Start Date',
      apiRef: 'event_schedule.campaign_start_date',
      dataType: DATA_TYPES.DATE,
      searchType: SEARCH_TYPES.DATE,
      formatOptions: {
        dateFormat: 'datePatternPST',
      },
      columnDefinition: {
        type: COLUMN_TYPES.DATE,
        colProps: {
          ...defaultColPropsWithWidth300,
          isVisible: true,
          sort: COLUMN_SORT_SETTING.NOT_SORTED,
          searchable: true,
        },
      },
    },
    {
      name: 'Type',
      apiRef: 'event_type',
      searchType: SEARCH_TYPES.SUGGEST_SYNC,
      options: eventTypeList,
      formatOptions: {
        titleCase: true,
      },
      columnDefinition: {
        type: COLUMN_TYPES.TEXT,
        colProps: {
          ...defaultColPropsWithWidth100,
          isVisible: true,
          sort: COLUMN_SORT_SETTING.NOT_SORTED,
          searchable: true,
        },
      },
    },
    {
      name: 'Trustee Sale',
      apiRef: 'trustee_sale',
      dataType: DATA_TYPES.BOOLEAN,
      searchType: SEARCH_TYPES.SUGGEST_SYNC,
      options: yesNoList,
      columnDefinition: {
        type: COLUMN_TYPES.BOOL,
        colProps: {
          ...defaultColPropsWithWidth100,
          isVisible: true,
          sort: COLUMN_SORT_SETTING.NOT_SORTED,
          searchable: true,
        },
      },
    },
    {
      name: 'Web Live Status',
      apiRef: 'event_configuration.web_live_status',
      dataType: DATA_TYPES.BOOLEAN,
      searchType: SEARCH_TYPES.SUGGEST_SYNC,
      options: [
        { value: true, text: 'Active' },
        { value: false, text: 'Inactive' },
      ],
      columnDefinition: {
        type: COLUMN_TYPES.STATUS,
        positiveValue: 'Active',
        negativeValue: 'Inactive',
        colProps: {
          ...defaultColPropsWithWidth100,
          isVisible: true,
          sort: COLUMN_SORT_SETTING.NOT_SORTED,
          searchable: true,
        },
      },
    },
    {
      apiRef: 'event_configuration.is_offer_enabled',
      name: 'Direct Offer Enabled',
      dataType: DATA_TYPES.BOOLEAN,
      searchType: SEARCH_TYPES.SUGGEST_SYNC,
      options: yesNoList,
      columnDefinition: {
        type: COLUMN_TYPES.BOOL,
        colProps: {
          ...defaultColPropsWithWidth100,
          searchable: true,
        },
      },
    },
    {
      name: 'Venue Count',
      apiRef: 'venue_count',
      columnDefinition: {
        type: COLUMN_TYPES.TEXT,
        colProps: { ...defaultColPropsWithWidth100, isVisible: true },
      },
    },
    {
      name: 'Listing Count',
      apiRef: 'listing_count', // don't have values from api yet
      isUiOnly: true,
      columnDefinition: {
        type: COLUMN_TYPES.TEXT,
        colProps: { ...defaultColPropsWithWidth100, isVisible: true },
      },
    },
    {
      name: 'Reserve Sum',
      apiRef: 'reserve_sum', // don't have values from api yet
      isUiOnly: true,
      columnDefinition: {
        type: COLUMN_TYPES.TEXT,
        colProps: { ...defaultColPropsWithWidth100, isVisible: true },
      },
    },
    {
      name: 'Registered Bidders',
      apiRef: 'registered_bidders', // don't have values from api yet
      isUiOnly: true,
      columnDefinition: {
        type: COLUMN_TYPES.TEXT,
        colProps: { ...defaultColPropsWithWidth300, isVisible: true },
      },
    },
    {
      name: 'Coming Soon',
      apiRef: 'event_configuration.coming_soon',
      dataType: DATA_TYPES.BOOLEAN,
      searchType: SEARCH_TYPES.SUGGEST_SYNC,
      options: yesNoList,
      columnDefinition: {
        type: COLUMN_TYPES.BOOL,
        colProps: { ...defaultColPropsWithWidth300, searchable: true },
      },
    },
    {
      name: 'Created Date',
      apiRef: 'created_date',
      dataType: DATA_TYPES.DATETIME_AS_LOCAL,
      columnDefinition: {
        type: COLUMN_TYPES.DATE,
        colProps: {
          ...defaultColPropsWithWidth300,
          sort: COLUMN_SORT_SETTING.NOT_SORTED,
        },
      },
    },
    {
      name: 'Created By',
      apiRef: 'created_by',
      foreignKey: LOOKUP_TYPES.USER,
      columnDefinition: {
        type: COLUMN_TYPES.TEXT,
        colProps: defaultColPropsWithWidth100,
      },
    },
    {
      name: 'Updated Date',
      apiRef: 'update_date',
      dataType: DATA_TYPES.DATETIME_AS_LOCAL,
      columnDefinition: {
        type: COLUMN_TYPES.DATE,
        colProps: {
          ...defaultColPropsWithWidth300,
          isVisible: true,
          sort: COLUMN_SORT_SETTING.NOT_SORTED,
        },
      },
    },
    {
      name: 'Updated By',
      apiRef: 'updated_by',
      foreignKey: LOOKUP_TYPES.USER,
      columnDefinition: {
        type: COLUMN_TYPES.TEXT,
        colProps: defaultColPropsWithWidth100,
      },
    },
    {
      name: 'Event ID',
      apiRef: 'event_id',
      columnDefinition: {
        type: COLUMN_TYPES.TEXT,
        dataType: DATA_TYPES.NUMBER,
        colProps: {
          ...defaultColPropsWithWidth100,
          isVisible: false,
          sort: COLUMN_SORT_SETTING.NOT_SORTABLE,
          searchable: true,
        },
      },
    },
    {
      name: 'Edit',
      apiRef: '__actions__', // Not api val
      isUiOnly: true, // exclude this field from ES DSL search list
      columnDefinition: {
        type: COLUMN_TYPES.ACTION,
        actions: ['Link'],
        colProps: { width: 50, fixedRight: true, align: 'center' },
      },
    },
  ],
  filterClauses: [],
  sortBy: [
    {
      field: 'title.lowercase',
      order: 'asc',
    },
  ],
}

const linkColumnAction = () => (actionIndex, rowIndex, primaryId) =>
  (
    <Link
      style={{ margin: -8, padding: 8 }}
      key={actionIndex}
      to={`/schedule/events/${primaryId}/edit`}
      data-elm-id={`action_edit_${rowIndex}`}
    >
      <Icon
        size='large'
        style={{ marginTop: -3 }}
        color='info'
        name='write square'
      />
    </Link>
  )

/**
 *
 * @param {*} fieldDefinitions
 * THIS SELECTOR IS TO MAINTAIN BACKWARDS COMPATIBILITY WITH EVERYTHING IN TOOLKIT
 * It will change/ be removed when everything there changes. This is so we can refactor slowly
 */
export const columnSelector = state =>
  state.fieldDefinitions
    .filter(field => field.columnDefinition)
    .map(col => {
      const column = {
        ...col.columnDefinition,
        ...{ field: col.apiRef, title: col.name },
      }
      if (column.actions) column.actions = column.actions.map(linkColumnAction)
      if (col.dataType) column.dataType = col.dataType
      if (col.searchType) column.searchType = col.searchType
      if (col.options) column.options = col.options
      if (col.formatOptions) column.formatOptions = col.formatOptions
      return column
    })

export const getFilterClauses = state => state.filterClauses

export default reducers.dataTable.dataTable(EVENTS, tableConfig)
